import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../../utils/api";
import { toast } from "react-toastify";
import AdminNav from "../../components/AdminNav";
import RichTextEditor from "../../components/RichTextEditor";
import { createPaymentMethod, normalizePaymentMethods } from "../../utils/paymentMethods";
import { createCustomProductBanner, createDefaultProductBanners, createProductBanner, getMissingProductBannerTypes, getProductBannerLabel, SYSTEM_PRODUCT_BANNER_IDS, normalizeProductBanners } from "../../utils/productBanners";
import { createShippingMethod, normalizeShippingMethods } from "../../utils/shippingMethods";
import { normalizeProductOptionDefinitions } from "../../utils/productOptions";

const DEFAULT_ABOUT_US_CONTENT = `<p>TabramEyewear is a leading wholesale supplier of premium eyewear to independent opticians across Ireland and the UK.</p><p>With years of experience in the industry, we pride ourselves on delivering quality frames at competitive trade prices with exceptional customer service.</p>`;
const DEFAULT_PRIVACY_POLICY_CONTENT = `<p>Last updated: March 15, 2026</p><h2>Introduction</h2><p>TabramEyewear is committed to protecting your privacy. This policy outlines how we collect, use, and protect your personal information.</p><h2>Data Collection</h2><p>We collect information necessary to process your trade account application and fulfill orders, including business details, contact information, and shipping addresses.</p>`;
const DEFAULT_AFTER_SALES_CONTENT = `<h2>Returns &amp; Exchanges</h2><p>We offer a 30-day return policy on all products. Items must be in original condition with all packaging intact.</p><h2>Warranty</h2><p>All frames come with a manufacturer's warranty against defects. Contact us for warranty claims.</p><h2>Contact Support</h2><p>For after-sale support, email us at support@tabrams.com or call +353 1 234 5678.</p>`;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

const createProductOptionSet = () => ({
  id: `option_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  options: [],
  optionsInput: "",
});

const prepareProductOptionSetsForEditing = (optionSets = []) =>
  normalizeProductOptionDefinitions(optionSets).map((optionSet) => ({
    ...optionSet,
    optionsInput: optionSet.options.join(", "),
  }));

export default function AdminSettings() {
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [togglingCategoryId, setTogglingCategoryId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteInfoSettings, setSiteInfoSettings] = useState({
    site_name: "TabramEyewear",
    min_order_value: 200,
    currency: "EUR",
    display_currencies: ["EUR"],
    shipping_countries: ["Ireland", "Northern Ireland", "United Kingdom"],
    payment_methods: [],
    shipping_methods: [],
    product_option_sets: [],
    product_banners: createDefaultProductBanners(),
    about_us_content: DEFAULT_ABOUT_US_CONTENT,
    privacy_policy_content: DEFAULT_PRIVACY_POLICY_CONTENT,
    after_sales_content: DEFAULT_AFTER_SALES_CONTENT,
  });
  const handleProductOptionSetChange = (optionSetId, field, value) => {
    setSiteInfoSettings((prev) => ({
      ...prev,
      product_option_sets: prev.product_option_sets.map((optionSet) => {
        if (optionSet.id !== optionSetId) {
          return optionSet;
        }

        if (field === "options") {
          return {
            ...optionSet,
            optionsInput: value,
          };
        }

        return {
          ...optionSet,
          [field]: value,
        };
      }),
    }));
  };

  const addProductOptionSet = () => {
    setSiteInfoSettings((prev) => ({
      ...prev,
      product_option_sets: [...prev.product_option_sets, createProductOptionSet()],
    }));
  };

  const removeProductOptionSet = (optionSetId) => {
    setSiteInfoSettings((prev) => ({
      ...prev,
      product_option_sets: prev.product_option_sets.filter((optionSet) => optionSet.id !== optionSetId),
    }));
  };
  const handleShippingMethodChange = (shippingMethodId, field, value) => {
    setSiteInfoSettings((prev) => ({
      ...prev,
      shipping_methods: prev.shipping_methods.map((method) => {
        if (method.id !== shippingMethodId) {
          return method;
        }

        return {
          ...method,
          [field]: value,
        };
      }),
    }));
  };

  const addShippingMethod = () => {
    setSiteInfoSettings((prev) => ({
      ...prev,
      shipping_methods: [...prev.shipping_methods, createShippingMethod()],
    }));
  };

  const removeShippingMethod = (shippingMethodId) => {
    setSiteInfoSettings((prev) => ({
      ...prev,
      shipping_methods: prev.shipping_methods.filter((method) => method.id !== shippingMethodId),
    }));
  };
  const handlePaymentMethodChange = (paymentMethodId, value) => {
    setSiteInfoSettings((prev) => ({
      ...prev,
      payment_methods: prev.payment_methods.map((method) => {
        if (method.id !== paymentMethodId) {
          return method;
        }

        return {
          ...method,
          name: value,
        };
      }),
    }));
  };

  const addPaymentMethod = () => {
    setSiteInfoSettings((prev) => ({
      ...prev,
      payment_methods: [...prev.payment_methods, createPaymentMethod()],
    }));
  };

  const removePaymentMethod = (paymentMethodId) => {
    setSiteInfoSettings((prev) => ({
      ...prev,
      payment_methods: prev.payment_methods.filter((method) => method.id !== paymentMethodId),
    }));
  };
  const [minOrderValueInput, setMinOrderValueInput] = useState("200");
  const [selectedCurrencies, setSelectedCurrencies] = useState(["EUR"]);
  const [shippingCountriesInput, setShippingCountriesInput] = useState("Ireland, Northern Ireland, United Kingdom");
  const [savingSiteInfo, setSavingSiteInfo] = useState(false);
  const [savingBanners, setSavingBanners] = useState(false);
  const [savingStaticContent, setSavingStaticContent] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState({
    paypal_client_id: '',
    paypal_client_secret: '',
    paypal_mode: 'sandbox',
    mypos_sid: '',
    mypos_wallet_number: '',
    mypos_client_id: '',
    mypos_private_key_path: '',
    mypos_mode: 'test'
  });
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchAvailableCurrencies();
    fetchSiteInfoSettings();
    fetchPaymentSettings();
  }, []);

  const fetchAvailableCurrencies = async () => {
    try {
      const response = await fetch("https://api.frankfurter.dev/v2/currencies");
      const currencies = await response.json();
      setAvailableCurrencies(currencies || []);
    } catch (error) {
      console.error("Error fetching currencies:", error);
      toast.error("Failed to load available currencies");
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories/all");
      setCategories(data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const { data } = await api.get("/admin/settings/payment");
      if (data.settings) {
        setPaymentSettings(prev => ({ ...prev, ...data.settings }));
      }
    } catch (error) {
      console.error("Error fetching payment settings:", error);
      // Don't show error toast on initial load if settings don't exist yet
    }
  };

  const fetchSiteInfoSettings = async () => {
    try {
      const { data } = await api.get("/admin/settings/site");
      if (data.settings) {
          setSiteInfoSettings((prev) => ({
            ...prev,
            ...data.settings,
            payment_methods: normalizePaymentMethods(data.settings.payment_methods || []),
            product_banners: normalizeProductBanners(data.settings.product_banners),
            shipping_methods: normalizeShippingMethods(data.settings.shipping_methods || []),
            product_option_sets: prepareProductOptionSetsForEditing(data.settings.product_option_sets || []),
          }));
        setMinOrderValueInput(String(data.settings.min_order_value ?? 200));
        setSelectedCurrencies(data.settings.display_currencies || ["EUR"]);
        setShippingCountriesInput((data.settings.shipping_countries || []).join(", "));
      }
    } catch (error) {
      console.error("Error fetching site settings:", error);
    }
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSiteContentChange = (key) => (value) => {
    setSiteInfoSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleProductBannerChange = (bannerKey, field, value) => {
    setSiteInfoSettings((prev) => ({
      ...prev,
      product_banners: {
        ...prev.product_banners,
        [bannerKey]: {
          ...prev.product_banners[bannerKey],
          [field]: value,
        },
      },
    }));
  };

  const addProductBanner = (bannerKey) => {
    const defaultBanner = createProductBanner(bannerKey);

    if (!defaultBanner) {
      return;
    }

    setSiteInfoSettings((prev) => ({
      ...prev,
      product_banners: {
        ...prev.product_banners,
        [bannerKey]: defaultBanner,
      },
    }));
  };

  const addCustomProductBanner = () => {
    const { id, banner } = createCustomProductBanner();

    setSiteInfoSettings((prev) => ({
      ...prev,
      product_banners: {
        ...prev.product_banners,
        [id]: banner,
      },
    }));
  };

  const removeProductBanner = (bannerKey) => {
    setSiteInfoSettings((prev) => {
      const nextProductBanners = { ...prev.product_banners };
      delete nextProductBanners[bannerKey];

      return {
        ...prev,
        product_banners: nextProductBanners,
      };
    });
  };

  const savePaymentSettings = async (e) => {
    e.preventDefault();
    setSavingPayment(true);
    try {
      await api.put("/admin/settings/payment", paymentSettings);
      toast.success("Payment settings updated successfully");
    } catch (error) {
      console.error("Error saving payment settings:", error);
      toast.error("Failed to save payment settings");
    } finally {
      setSavingPayment(false);
    }
  };

  const saveSiteInfoSettings = async (e) => {
    e.preventDefault();
    setSavingSiteInfo(true);

    try {
      const shippingCountries = shippingCountriesInput
        .split(",")
        .map((country) => country.trim())
        .filter(Boolean);
      const minOrderValue = Number(minOrderValueInput);
      const displayCurrencies = Array.from(new Set(["EUR", ...selectedCurrencies]));
      const paymentMethods = normalizePaymentMethods(siteInfoSettings.payment_methods || []);
      const shippingMethods = normalizeShippingMethods(
        (siteInfoSettings.shipping_methods || []).map((method) => ({
          ...method,
          price: Number(method.price),
        })),
      );
      const rawProductOptionSets = siteInfoSettings.product_option_sets || [];
      const hasIncompleteProductOptionSets = rawProductOptionSets.some((optionSet) => {
        const name = String(optionSet?.name || "").trim();
        const optionCount = String(optionSet?.optionsInput || "")
          .split(/\r?\n|,/) 
          .map((entry) => entry.trim())
          .filter(Boolean).length;

        return (name && optionCount === 0) || (!name && optionCount > 0);
      });
      const productOptionSets = normalizeProductOptionDefinitions(
        rawProductOptionSets.map((optionSet) => ({
          ...optionSet,
          options: String(optionSet?.optionsInput || "")
            .split(/\r?\n|,/) 
            .map((entry) => entry.trim())
            .filter(Boolean),
        })),
      );

      if (!Number.isFinite(minOrderValue) || minOrderValue < 0) {
        toast.error("Minimum order value must be a valid amount");
        setSavingSiteInfo(false);
        return;
      }

      if (paymentMethods.length === 0) {
        toast.error("Add at least one payment method before saving");
        setSavingSiteInfo(false);
        return;
      }

      if (paymentMethods.length !== (siteInfoSettings.payment_methods || []).length) {
        toast.error("Complete or remove each payment method before saving");
        setSavingSiteInfo(false);
        return;
      }

      if (shippingMethods.length === 0) {
        toast.error("Add at least one shipping method before saving");
        setSavingSiteInfo(false);
        return;
      }

      if (shippingMethods.length !== (siteInfoSettings.shipping_methods || []).length) {
        toast.error("Complete or remove each shipping method before saving");
        setSavingSiteInfo(false);
        return;
      }

      if (hasIncompleteProductOptionSets) {
        toast.error("Complete or remove each product option set before saving");
        setSavingSiteInfo(false);
        return;
      }

      await api.put("/admin/settings/site", {
        display_currencies: displayCurrencies,
        min_order_value: minOrderValue,
        payment_methods: paymentMethods,
        product_option_sets: productOptionSets,
        shipping_methods: shippingMethods,
        shipping_countries: shippingCountries,
      });

      setSiteInfoSettings((prev) => ({
        ...prev,
        display_currencies: displayCurrencies,
        min_order_value: minOrderValue,
        payment_methods: paymentMethods,
        product_option_sets: prepareProductOptionSetsForEditing(productOptionSets),
        shipping_methods: shippingMethods,
        shipping_countries: shippingCountries,
      }));
      setSelectedCurrencies(displayCurrencies);
      setMinOrderValueInput(String(minOrderValue));
      setShippingCountriesInput(shippingCountries.join(", "));
      toast.success("Site information updated successfully");
    } catch (error) {
      console.error("Error saving site settings:", error);
      toast.error(error.response?.data?.error || "Failed to save site information");
    } finally {
      setSavingSiteInfo(false);
    }
  };

  const saveStaticContentSettings = async (e) => {
    e.preventDefault();
    setSavingStaticContent(true);

    try {
      await api.put("/admin/settings/site", {
        about_us_content: siteInfoSettings.about_us_content,
        privacy_policy_content: siteInfoSettings.privacy_policy_content,
        after_sales_content: siteInfoSettings.after_sales_content,
      });

      toast.success("Static page content updated successfully");
    } catch (error) {
      console.error("Error saving static page content:", error);
      toast.error(error.response?.data?.error || "Failed to save static page content");
    } finally {
      setSavingStaticContent(false);
    }
  };

  const saveProductBannerSettings = async (e) => {
    e.preventDefault();
    setSavingBanners(true);

    try {
      const productBanners = normalizeProductBanners(siteInfoSettings.product_banners);

      await api.put("/admin/settings/site", {
        product_banners: productBanners,
      });

      setSiteInfoSettings((prev) => ({
        ...prev,
        product_banners: productBanners,
      }));
      toast.success("Product banners updated successfully");
    } catch (error) {
      console.error("Error saving product banners:", error);
      toast.error(error.response?.data?.error || "Failed to save product banners");
    } finally {
      setSavingBanners(false);
    }
  };

  const toggleCategoryVisibility = async (categoryId, currentVisibility) => {
    setTogglingCategoryId(categoryId);
    try {
      await api.put(`/categories/${categoryId}/visibility`, {
        isVisible: !currentVisibility,
      });
      toast.success("Category visibility updated");
      await fetchCategories();
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error("Failed to update category");
    } finally {
      setTogglingCategoryId(null);
    }
  };

  const createCategory = async (e) => {
    e.preventDefault();
    const trimmedName = newCategoryName.trim();

    if (!trimmedName) {
      toast.error("Category name is required");
      return;
    }

    setSavingCategory(true);
    try {
      await api.post("/categories", { name: trimmedName });
      setNewCategoryName("");
      toast.success("Category added successfully");
      await fetchCategories();
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error(error.response?.data?.error || "Failed to create category");
    } finally {
      setSavingCategory(false);
    }
  };

  const deleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete category "${categoryName}"? This will remove its product assignments.`)) {
      return;
    }

    setDeletingCategoryId(categoryId);
    try {
      await api.delete(`/categories/${categoryId}`);
      toast.success("Category deleted successfully");
      await fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(error.response?.data?.error || "Failed to delete category");
    } finally {
      setDeletingCategoryId(null);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <AdminNav />
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="heading-metallic text-4xl mb-8">Site Settings</h1>

          {/* Category Management */}
          <div className="card mb-8">
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="heading-metallic-soft text-2xl mb-2">Category Management</h2>
                <p className="text-gray-400">Add or remove categories and control which ones are visible in the header menu and product filters.</p>
              </div>
              <form onSubmit={createCategory} className="flex w-full max-w-md gap-3">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Add a category"
                  className="input-field flex-1"
                />
                <button type="submit" disabled={savingCategory} className="btn-primary whitespace-nowrap">
                  {savingCategory ? "Adding..." : "Add Category"}
                </button>
              </form>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="text-gold-500">Loading categories...</div>
              </div>
            ) : (
              <div className="space-y-4">
                {categories.map((category) => (
                  <div key={category.id} className="flex flex-col gap-4 rounded-lg border border-gray-700 bg-gradient-to-r from-black via-gray-900 to-black p-4 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <h3 className="font-bold text-lg">{category.name}</h3>
                      <p className="text-sm text-gray-400">Slug: {category.slug}</p>
                    </div>
                    <div className="flex items-center gap-4 self-end md:self-auto">
                      <label className="flex items-center gap-3 text-sm text-gray-300">
                        <span>{category.is_visible ? "Visible" : "Hidden"}</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={category.is_visible}
                          onClick={() => toggleCategoryVisibility(category.id, category.is_visible)}
                          disabled={togglingCategoryId === category.id}
                          className={`relative inline-flex h-7 w-14 items-center rounded-full border transition-all duration-200 ${category.is_visible ? "border-green-500 bg-green-600/80" : "border-gray-600 bg-gray-800"} ${togglingCategoryId === category.id ? "opacity-60" : ""}`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${category.is_visible ? "translate-x-7" : "translate-x-1"}`}
                          />
                        </button>
                      </label>
                      <button
                        type="button"
                        onClick={() => deleteCategory(category.id, category.name)}
                        disabled={deletingCategoryId === category.id}
                        className="rounded px-4 py-2 font-medium text-white transition-colors duration-200 hover:bg-red-700 bg-red-600 disabled:opacity-60"
                      >
                        {deletingCategoryId === category.id ? "Removing..." : "Remove"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card mb-8">
            <form onSubmit={saveProductBannerSettings} className="space-y-6">
              <div>
                <h2 className="heading-metallic-soft text-2xl mb-2">Product Banners</h2>
                <p className="text-gray-400">Manage the banner name and background colour for featured products and new arrivals.</p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={addCustomProductBanner} className="btn-primary">
                  Add Banner
                </button>

                {getMissingProductBannerTypes(siteInfoSettings.product_banners).length > 0 &&
                  getMissingProductBannerTypes(siteInfoSettings.product_banners).map((banner) => (
                    <button
                      key={banner.id}
                      type="button"
                      onClick={() => addProductBanner(banner.id)}
                      className="btn-secondary"
                    >
                      Add {banner.label} Banner
                    </button>
                  ))}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(siteInfoSettings.product_banners || {}).map(([bannerId, bannerConfig]) => (
                  <div key={bannerId} className="rounded-lg border border-gray-700 bg-gradient-to-r from-black via-gray-900 to-black p-4">
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-gray-300">{getProductBannerLabel(bannerId, bannerConfig)} Banner</h3>
                        {!SYSTEM_PRODUCT_BANNER_IDS.includes(bannerId) && <p className="mt-1 text-xs text-gray-500">Custom banner</p>}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeProductBanner(bannerId)}
                        className="text-sm text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-300">Banner Name</label>
                        <input
                          type="text"
                          value={bannerConfig?.name || ""}
                          onChange={(e) => handleProductBannerChange(bannerId, "name", e.target.value)}
                          className="w-full rounded border border-gray-700 bg-black px-4 py-2 text-white focus:outline-none focus:border-gold"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-300">Background Colour</label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={bannerConfig?.backgroundColor || "#111827"}
                            onChange={(e) => handleProductBannerChange(bannerId, "backgroundColor", e.target.value)}
                            className="h-12 w-16 cursor-pointer rounded border border-gray-700 bg-black p-1"
                          />
                          <div
                            className="flex-1 rounded border border-gray-700 px-4 py-3 text-sm font-semibold text-white"
                            style={{ backgroundColor: bannerConfig?.backgroundColor || "#111827" }}
                          >
                            {bannerConfig?.name || "New Banner"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {Object.keys(siteInfoSettings.product_banners || {}).length === 0 && (
                <div className="rounded-lg border border-dashed border-gray-600 px-4 py-6 text-sm text-gray-400">
                  No product banners configured.
                </div>
              )}

              <div className="flex justify-end">
                <button type="submit" disabled={savingBanners} className="btn-primary">
                  {savingBanners ? "Saving..." : "Save Product Banners"}
                </button>
              </div>
            </form>
          </div>

          {/* Site Information */}
          <div className="card mb-8">
            <h2 className="heading-metallic-soft text-2xl mb-6">Site Information</h2>
            <form onSubmit={saveSiteInfoSettings} className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gradient-to-r from-black via-gray-900 to-black rounded">
                <div>
                  <p className="text-sm text-gray-400">Minimum Order Value</p>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={minOrderValueInput}
                    onChange={(e) => setMinOrderValueInput(e.target.value)}
                    className="mt-2 w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Base Product Currency</p>
                  <p className="font-medium">{siteInfoSettings.currency} (product uploads stay in EUR)</p>
                </div>
              </div>
              <div className="p-4 bg-gradient-to-r from-black via-gray-900 to-black rounded border border-gray-700">
                <label className="block text-sm font-medium text-gray-300 mb-2">Display Currencies</label>
                <select
                  multiple
                  value={selectedCurrencies}
                  onChange={(e) => {
                    const values = Array.from(e.target.selectedOptions, (option) => option.value);
                    setSelectedCurrencies(Array.from(new Set(["EUR", ...values])));
                  }}
                  className="h-48 w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                >
                  {availableCurrencies.map((currency) => (
                    <option key={currency.iso_code} value={currency.iso_code}>
                      {currency.iso_code} - {currency.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">Hold Command on Mac to select multiple currencies. EUR is always included.</p>
              </div>
              <div className="p-4 bg-gradient-to-r from-black via-gray-900 to-black rounded border border-gray-700">
                <label className="block text-sm font-medium text-gray-300 mb-2">Shipping Countries</label>
                <input
                  type="text"
                  value={shippingCountriesInput}
                  onChange={(e) => setShippingCountriesInput(e.target.value)}
                  placeholder="Ireland, Northern Ireland, United Kingdom"
                  className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                />
                <p className="text-xs text-gray-500 mt-2">Comma-separated list used by the checkout shipping and billing country dropdowns.</p>
              </div>
              <div className="space-y-4 border border-gray-700 rounded-lg p-6 bg-gradient-to-r from-black via-gray-900 to-black">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="heading-metallic-soft text-xl mb-2">Payment Methods</h3>
                    <p className="text-sm text-gray-400">Create the payment methods available for product assignment and user permissions.</p>
                  </div>
                  <button type="button" onClick={addPaymentMethod} className="btn-secondary">
                    Add Payment Method
                  </button>
                </div>

                {siteInfoSettings.payment_methods.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-600 px-4 py-6 text-sm text-gray-400">
                    No payment methods configured yet.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {siteInfoSettings.payment_methods.map((method, index) => (
                      <div key={method.id} className="rounded-lg border border-gray-700 bg-black/40 p-4">
                        <div className="mb-4 flex items-start justify-between gap-4">
                          <p className="text-sm font-medium text-gray-300">Payment Method {index + 1}</p>
                          <button type="button" onClick={() => removePaymentMethod(method.id)} className="text-sm text-red-400 hover:text-red-300">
                            Remove
                          </button>
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-300">Method Name</label>
                          <input
                            type="text"
                            value={method.name}
                            onChange={(e) => handlePaymentMethodChange(method.id, e.target.value)}
                            placeholder="e.g. PayPal"
                            className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-4 border border-gray-700 rounded-lg p-6 bg-gradient-to-r from-black via-gray-900 to-black">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="heading-metallic-soft text-xl mb-2">Shipping Methods</h3>
                    <p className="text-sm text-gray-400">Add the shipping methods customers can choose from at checkout, with their delivery duration and price.</p>
                  </div>
                  <button type="button" onClick={addShippingMethod} className="btn-secondary">
                    Add Shipping Method
                  </button>
                </div>

                {siteInfoSettings.shipping_methods.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-600 px-4 py-6 text-sm text-gray-400">
                    No shipping methods configured yet.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {siteInfoSettings.shipping_methods.map((method, index) => (
                      <div key={method.id} className="rounded-lg border border-gray-700 bg-black/40 p-4">
                        <div className="mb-4 flex items-start justify-between gap-4">
                          <p className="text-sm font-medium text-gray-300">Shipping Method {index + 1}</p>
                          <button type="button" onClick={() => removeShippingMethod(method.id)} className="text-sm text-red-400 hover:text-red-300">
                            Remove
                          </button>
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                          <div>
                            <label className="mb-2 block text-sm font-medium text-gray-300">Method Name</label>
                            <input
                              type="text"
                              value={method.name}
                              onChange={(e) => handleShippingMethodChange(method.id, "name", e.target.value)}
                              placeholder="e.g. Standard Delivery"
                              className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-gray-300">Duration</label>
                            <input
                              type="text"
                              value={method.duration}
                              onChange={(e) => handleShippingMethodChange(method.id, "duration", e.target.value)}
                              placeholder="e.g. 2-3 working days"
                              className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-gray-300">Price</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={method.price}
                              onChange={(e) => handleShippingMethodChange(method.id, "price", e.target.value)}
                              placeholder="0.00"
                              className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-4 border border-gray-700 rounded-lg p-6 bg-gradient-to-r from-black via-gray-900 to-black">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="heading-metallic-soft text-xl mb-2">Product Form Variables</h3>
                    <p className="text-sm text-gray-400">Create reusable option sets like Size or Frame Colour, then choose which values are available on each product.</p>
                  </div>
                  <button type="button" onClick={addProductOptionSet} className="btn-secondary">
                    Add Variable
                  </button>
                </div>

                {siteInfoSettings.product_option_sets.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-600 px-4 py-6 text-sm text-gray-400">
                    No product form variables configured yet.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {siteInfoSettings.product_option_sets.map((optionSet, index) => (
                      <div key={optionSet.id} className="rounded-lg border border-gray-700 bg-black/40 p-4">
                        <div className="mb-4 flex items-start justify-between gap-4">
                          <p className="text-sm font-medium text-gray-300">Variable {index + 1}</p>
                          <button type="button" onClick={() => removeProductOptionSet(optionSet.id)} className="text-sm text-red-400 hover:text-red-300">
                            Remove
                          </button>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <label className="mb-2 block text-sm font-medium text-gray-300">Variable Name</label>
                            <input
                              type="text"
                              value={optionSet.name}
                              onChange={(e) => handleProductOptionSetChange(optionSet.id, "name", e.target.value)}
                              placeholder="e.g. Size"
                              className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-gray-300">Values</label>
                            <textarea
                              value={optionSet.optionsInput || ""}
                              onChange={(e) => handleProductOptionSetChange(optionSet.id, "options", e.target.value)}
                              placeholder="e.g. Small, Medium, Large"
                              rows="3"
                              className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                            />
                            <p className="mt-2 text-xs text-gray-500">Separate values with commas or line breaks.</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={savingSiteInfo}
                  className="btn-primary"
                >
                  {savingSiteInfo ? "Saving..." : "Save Site Information"}
                </button>
              </div>
            </form>
          </div>

          <div className="card mb-8">
            <h2 className="heading-metallic-soft text-2xl mb-6">Static Page Content</h2>
            <form onSubmit={saveStaticContentSettings} className="space-y-6">
              <div className="space-y-6 border border-gray-700 rounded-lg p-6 bg-gradient-to-r from-black via-gray-900 to-black">
                <div>
                  <p className="text-sm text-gray-400">Update the content shown on the About Us, Privacy Policy, and After Sales pages linked in the footer.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">About Us</label>
                  <div className="bg-white text-black rounded-xl overflow-hidden border border-gray-700">
                    <RichTextEditor
                      value={siteInfoSettings.about_us_content}
                      onChange={handleSiteContentChange("about_us_content")}
                      modules={quillModules}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Privacy Policy</label>
                  <div className="bg-white text-black rounded-xl overflow-hidden border border-gray-700">
                    <RichTextEditor
                      value={siteInfoSettings.privacy_policy_content}
                      onChange={handleSiteContentChange("privacy_policy_content")}
                      modules={quillModules}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">After Sales</label>
                  <div className="bg-white text-black rounded-xl overflow-hidden border border-gray-700">
                    <RichTextEditor
                      value={siteInfoSettings.after_sales_content}
                      onChange={handleSiteContentChange("after_sales_content")}
                      modules={quillModules}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={savingStaticContent}
                  className="btn-primary"
                >
                  {savingStaticContent ? "Saving..." : "Save Static Page Content"}
                </button>
              </div>
            </form>
          </div>

          {/* Email Settings */}
          <div className="card mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h2 className="heading-metallic-soft text-2xl">Email Settings</h2>
                <p className="text-gray-400 mt-2">Operational email settings and template CRUD now live in the dedicated Admin Emails module.</p>
              </div>
              <Link
                to="/admin/emails"
                className="btn-primary"
              >
                Open Admin Emails
              </Link>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-black via-gray-900 to-black rounded">
                <p className="text-sm text-gray-400 mb-2">Email Mode</p>
                <p className="font-medium mb-2">Managed in Admin Emails</p>
                <p className="text-sm text-gray-500">Use the new Admin Emails page to manage sender addresses, enable or disable delivery, test templates, and edit built-in transactional emails.</p>
              </div>
              <div className="p-4 bg-gradient-to-r from-black via-gray-900 to-black rounded">
                <p className="text-sm text-gray-400 mb-2">From Email</p>
                <p className="font-medium">Configured in Admin Emails</p>
              </div>
            </div>
          </div>

          {/* Payment Credentials */}
          <div className="card">
            <h2 className="heading-metallic-soft text-2xl mb-6">Payment Credentials</h2>
            <p className="text-gray-400 mb-6">Configure PayPal and MyPos payment gateway credentials. These settings are stored securely in the database.</p>
            
            <form onSubmit={savePaymentSettings} className="space-y-8">
              {/* PayPal Settings */}
              <div className="border border-gray-700 rounded-lg p-6 bg-gradient-to-r from-black via-gray-900 to-black">
                <h3 className="heading-metallic-soft text-xl mb-4">PayPal Configuration</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Mode
                    </label>
                    <select
                      name="paypal_mode"
                      value={paymentSettings.paypal_mode}
                      onChange={handlePaymentChange}
                      className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                    >
                      <option value="sandbox">Sandbox (Testing)</option>
                      <option value="live">Live (Production)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Client ID
                    </label>
                    <input
                      type="text"
                      name="paypal_client_id"
                      value={paymentSettings.paypal_client_id}
                      onChange={handlePaymentChange}
                      placeholder="Enter PayPal Client ID"
                      className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Client Secret
                    </label>
                    <input
                      type="password"
                      name="paypal_client_secret"
                      value={paymentSettings.paypal_client_secret}
                      onChange={handlePaymentChange}
                      placeholder="Enter PayPal Client Secret"
                      className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                    />
                  </div>
                </div>
              </div>

              {/* MyPos Settings */}
              <div className="border border-gray-700 rounded-lg p-6 bg-gradient-to-r from-black via-gray-900 to-black">
                <h3 className="heading-metallic-soft text-xl mb-4">MyPos Configuration</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Mode
                    </label>
                    <select
                      name="mypos_mode"
                      value={paymentSettings.mypos_mode}
                      onChange={handlePaymentChange}
                      className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                    >
                      <option value="test">Test Mode</option>
                      <option value="live">Live Mode</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Store ID (SID)
                    </label>
                    <input
                      type="text"
                      name="mypos_sid"
                      value={paymentSettings.mypos_sid}
                      onChange={handlePaymentChange}
                      placeholder="Enter MyPos Store ID"
                      className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Wallet Number
                    </label>
                    <input
                      type="text"
                      name="mypos_wallet_number"
                      value={paymentSettings.mypos_wallet_number}
                      onChange={handlePaymentChange}
                      placeholder="Enter MyPos Wallet Number"
                      className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Client ID
                    </label>
                    <input
                      type="text"
                      name="mypos_client_id"
                      value={paymentSettings.mypos_client_id}
                      onChange={handlePaymentChange}
                      placeholder="Enter MyPos Client ID"
                      className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Private Key Path
                    </label>
                    <input
                      type="text"
                      name="mypos_private_key_path"
                      value={paymentSettings.mypos_private_key_path}
                      onChange={handlePaymentChange}
                      placeholder="e.g., /path/to/mypos-private-key.pem"
                      className="w-full bg-black border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-gold"
                    />
                    <p className="text-xs text-gray-500 mt-1">Full path to your MyPos RSA private key file</p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={savingPayment}
                  className="btn-primary"
                >
                  {savingPayment ? 'Saving...' : 'Save Payment Settings'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
